import { useEffect } from 'react';
import { nanoid } from 'nanoid';
import {
  Emogiface,
  Emogiface2,
  Emogiface3,
  Emogiface4,
  Emogiface5,
  Emogiface6,
} from '../tools/MyIcons';

// ─── Constants ───────────────────────────────────────────────────────────────
export const COMMENT_MAX_LENGTH = 200;
export const CACHE_TTL_MS = 5 * 60 * 1000;
export const LS_USER_ID_KEY = 'survey_uid';
export const THANKS_DELAY_MS = 3000;

// Tiempo de "respiro" antes de avanzar automáticamente a la siguiente
// pregunta en layouts de un paso a la vez (ej. stepped), para que el
// usuario alcance a ver su selección marcada antes del cambio de tarjeta.
export const AUTO_ADVANCE_DELAY_MS = 380;

// Type usado cuando `survey.type` no coincide con ningún layout registrado
// (o está ausente). Ver formLayoutRegistry en SurveyFormLayouts.jsx.
export const DEFAULT_SURVEY_TYPE = 0;

export const EMOJI_SETS = {
  emojis:        Emogiface,
  emojis2:       Emogiface2,
  emojisCat:     Emogiface3,
  emojispeople:  Emogiface4,
  emojisnumbers: Emogiface5,
  emojislike:    Emogiface6,
};

export const EMOJI_TYPES = new Set(Object.keys(EMOJI_SETS));

// Lookup inverso precalculado una sola vez a nivel de módulo: de "número de
// emoji" (el mismo valor que se guarda en `answers[qId]`, ej. "3") a la URL
// del ícono correspondiente, por tipo de set. Antes este mapeo se rearmaba
// en cada render con Object.entries(set).find(...) + regex, tanto en
// QuestionCard (lista completa de emojis) como en AnswerBadge de
// SurveyFormLayouts.jsx (burbuja de resumen del flujo stepped). Al ser
// EMOJI_SETS estático, el resultado es siempre el mismo, así que se computa
// una única vez acá y ambos componentes solo hacen una lectura O(1).
export const EMOJI_VALUE_LOOKUP = Object.fromEntries(
  Object.entries(EMOJI_SETS).map(([type, set]) => [
    type,
    Object.fromEntries(
      Object.entries(set).map(([key, value]) => [
        key.match(/face(\d+)/)?.[1] ?? key,
        value,
      ])
    ),
  ])
);

export const GLASS_SX = {
  background:     'rgba(255,255,255,0.22) !important',
  backdropFilter: 'blur(6px)',
  border:         '1px solid rgba(255,255,255,0.35)',
};

// ─── LocalStorage helpers ─────────────────────────────────────────────────────
export const lsSubmittedKey = (urlId) => `survey_submitted_${urlId}`;

export const getOrCreateUserId = () => {
  try {
    let uid = localStorage.getItem(LS_USER_ID_KEY);
    if (!uid) {
      uid = nanoid(12);
      localStorage.setItem(LS_USER_ID_KEY, uid);
    }
    return uid;
  } catch {
    return nanoid(12);
  }
};

export const hasAlreadySubmitted = (urlId) => {
  try {
    return localStorage.getItem(lsSubmittedKey(urlId)) === 'true';
  } catch {
    return false;
  }
};

export const markAsSubmitted = (urlId) => {
  try {
    localStorage.setItem(lsSubmittedKey(urlId), 'true');
  } catch {}
};

// ─── SessionStorage cache ─────────────────────────────────────────────────────
export const readCache = (urlId) => {
  try {
    const raw = sessionStorage.getItem(`survey_${urlId}`);
    if (!raw) return null;
    const { data, ts } = JSON.parse(raw);
    if (Date.now() - ts > CACHE_TTL_MS) {
      sessionStorage.removeItem(`survey_${urlId}`);
      return null;
    }
    return data;
  } catch {
    return null;
  }
};

export const writeCache = (urlId, data) => {
  try {
    sessionStorage.setItem(
      `survey_${urlId}`,
      JSON.stringify({ data, ts: Date.now() })
    );
  } catch {}
};

// ─── Pure helpers ─────────────────────────────────────────────────────────────
export const initializeAnswers = (questions) =>
  Object.entries(questions || {}).reduce((acc, [qId, q]) => {
    acc[qId] =
      q.type === 'rating'  ? 0 :
      q.type === 'slider'  ? (q.sliderConfig?.min ?? 0) :
      '';
    return acc;
  }, {});

export const isQuestionAnswered = (question, answer) => {
  switch (question.type) {
    case 'rating':  return answer > 0;
    case 'slider':  return answer !== null && answer !== undefined;
    case 'text':    return answer?.trim() !== '';
    case 'radio':   return answer !== '';
    default:
      return EMOJI_TYPES.has(question.type) ? answer !== '' : false;
  }
};

// ─── Viewport height (mobile) ─────────────────────────────────────────────────
// Fija --vh-100 en píxeles usando el alto real del viewport visual, en vez
// de depender únicamente de la unidad CSS `dvh`. Esto resuelve dos
// problemas en dispositivos de campo (Android/WebView variados):
//   1. Soporte inconsistente de `dvh` en navegadores/WebViews más viejos.
//   2. Saltos de layout cuando aparece el teclado en la pregunta de texto
//      del flujo stepped: `visualViewport` reacciona con el alto exacto
//      disponible (excluyendo el teclado), mientras que `100vh`/`100dvh`
//      puede tardar o no reflejarlo bien.
// El CSS (`SurveyResponse.css`) ya define --vh-100 con fallback a vh/dvh;
// este hook simplemente lo sobrescribe con un valor más preciso cuando el
// navegador lo permite.
export const useViewportHeight = () => {
  useEffect(() => {
    const setVh = () => {
      const h = window.visualViewport?.height ?? window.innerHeight;
      document.documentElement.style.setProperty('--vh-100', `${h}px`);
    };

    setVh();

    window.visualViewport?.addEventListener('resize', setVh);
    window.addEventListener('resize', setVh);
    window.addEventListener('orientationchange', setVh);

    return () => {
      window.visualViewport?.removeEventListener('resize', setVh);
      window.removeEventListener('resize', setVh);
      window.removeEventListener('orientationchange', setVh);
    };
  }, []);
};